package org.zq.astrock_ai.network

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.zq.astrock_ai.api.dto.AgentChatRequestDto
import org.zq.astrock_ai.api.dto.AgentChatResponseDto
import org.zq.astrock_ai.ui.research.gushi.data.StockTrendMetrics
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import kotlin.random.Random

/**
 * 客户端直连 Kimi / Moonshot（OpenAI 兼容）。
 * 文档：https://platform.kimi.com/docs/api/overview
 * 旗舰模型：kimi-k3（temperature 固定 1.0，勿传；用 reasoning_effort）。
 */
class MoonshotDirectClient(
    private val baseUrl: String = "https://api.moonshot.cn/v1",
) {
    private val client: HttpClient = createAiHttpClient().config {
        install(ContentNegotiation) {
            json(
                Json {
                    ignoreUnknownKeys = true
                    isLenient = true
                    encodeDefaults = false
                    explicitNulls = false
                },
            )
        }
    }

    suspend fun chat(
        request: AgentChatRequestDto,
        stock: StockItem,
        metrics: StockTrendMetrics,
        apiKey: String,
        model: String = moonshotModel(),
        reasoningEffort: String = moonshotReasoningEffort(),
    ): AgentChatResponseDto {
        val sessionId = request.sessionId?.takeIf { it.isNotBlank() }
            ?: "kimi-${stock.code}-${Random.nextInt(1000, 9999)}"
        require(apiKey.isNotBlank()) { "MOONSHOT_API_KEY 为空" }

        val context = request.researchBrief?.trim().takeIf { !it.isNullOrEmpty() } ?: buildString {
            appendLine("股票：${stock.name}（${stock.code}）")
            appendLine("现价：${stock.price}  涨跌：${stock.changePercent}")
            appendLine("技术状态：${metrics.status} / ${metrics.position}")
            appendLine("上行概率：${metrics.upsideProb}%  走弱概率：${metrics.weakenProb}%  风险：${metrics.riskLevel}")
            appendLine("分析摘要：${metrics.analysis}")
            appendLine("建议标题：${metrics.adviceTitle}")
            appendLine("仓位建议：${metrics.positionDesc}")
            appendLine("买入计划：${metrics.buyPlan}")
            appendLine("风险提示：${metrics.riskHint}")
            if (request.costPrice != null) appendLine("成本价：${request.costPrice}")
            if (request.quantity != null) appendLine("持仓数量：${request.quantity}")
        }

        val messages = listOf(
            MoonshotChatMessage(
                role = "system",
                content = """
                    你是「智启股势」，面向 A 股投资者的研究助手。
                    请用中文、白话、可执行地回答，不要编造未提供的数据。
                    必须先消化下方「当前研究台实时数据」，再回答用户问题。
                    回答结构建议：先确认已读到的关键数据 → 结论 → 依据 → 操作建议 → 风险。
                    不做保证收益承诺。
                """.trimIndent(),
            ),
            MoonshotChatMessage(role = "system", content = context),
            MoonshotChatMessage(role = "user", content = request.message),
        )

        // kimi-k3 优先；参数按官方约束：不传 temperature。
        val modelsToTry = listOf(model, "kimi-k3", "kimi-k2.5").distinct().filter { it.isNotBlank() }
        var lastError: String? = null
        for (candidate in modelsToTry) {
            val effort = if (candidate.startsWith("kimi-k3")) {
                reasoningEffort.ifBlank { "high" }
            } else {
                null
            }
            val result = runCatching {
                requestCompletion(apiKey, candidate, messages, effort)
            }
            val response = result.getOrNull()
            if (response != null) {
                val reply = response.choices.firstOrNull()?.message?.content.orEmpty().ifBlank {
                    "模型未返回有效内容，请稍后重试。"
                }
                return AgentChatResponseDto(
                    sessionId = sessionId,
                    replyText = reply,
                    toolsCalled = listOf("local_research_context", "kimi_chat:$candidate"),
                    dataTime = "直连 Kimi ($candidate)",
                    mode = "kimi-direct",
                )
            }
            lastError = result.exceptionOrNull()?.message ?: "未知错误"
            if (lastError.contains("401") || lastError.contains("Invalid Authentication", ignoreCase = true)) {
                break
            }
        }

        error(lastError ?: "Kimi 调用失败")
    }

    private suspend fun requestCompletion(
        apiKey: String,
        model: String,
        messages: List<MoonshotChatMessage>,
        reasoningEffort: String?,
    ): MoonshotChatResponse {
        val httpResponse = client.post("$baseUrl/chat/completions") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $apiKey")
            setBody(
                MoonshotChatRequest(
                    model = model,
                    messages = messages,
                    // kimi-k3：temperature 固定 1.0，显式传入其他值会报错，故不传
                    reasoningEffort = reasoningEffort,
                ),
            )
        }
        if (!httpResponse.status.isSuccess()) {
            val errBody = runCatching { httpResponse.bodyAsText() }.getOrNull().orEmpty()
            error("HTTP ${httpResponse.status.value} model=$model ${errBody.take(280)}")
        }
        return httpResponse.body()
    }
}

@Serializable
private data class MoonshotChatRequest(
    val model: String,
    val messages: List<MoonshotChatMessage>,
    @SerialName("reasoning_effort")
    val reasoningEffort: String? = null,
)

@Serializable
private data class MoonshotChatMessage(
    val role: String,
    val content: String? = null,
)

@Serializable
private data class MoonshotChatResponse(
    val choices: List<MoonshotChatChoice> = emptyList(),
)

@Serializable
private data class MoonshotChatChoice(
    val message: MoonshotChatMessage? = null,
)
